import React, { FC, Fragment, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  DatePickers,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  RadioGroup,
  Radio,
  Select,
  TextField,
  Tooltip,
  Divider,
  IconButton,
  Button,
  DriftIcons,
} from 'drift-ui';
import { RootState } from 'app/configureStore';
import { useDispatch, useSelector } from 'react-redux';
import { sendDOFirebaseEvent } from 'utils/utils';
import {
  AnalyticsEvents,
  AnalyticsPages,
  Applicant,
  phoneFormatter,
  binaryValueFormatter,
  useSectionFormHelpers,
  maskSSN,
  ssnFormatter,
  cityRegex,
  countyRegex,
  emailRegex,
  jobTitleRegex,
  nameRegex,
  phoneRegex,
  previousEmployerRegex,
  ssnOrItinRegex,
  STATE_OPTIONS,
  streetRegex,
  zipRegex,
  DO_APPLICATION_TYPE,
  findDuplicateIndices,
} from 'client-experience-toolbox';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import styles from './BaseFormStyles.scss';
import ownerStyles from './OwnerInformation.scss';
import TooltipIcon from '../../assets/images/info.svg';
import { useTranslation } from 'react-i18next';
import { ContinueButton } from '../../components/ContinueButton';
import { v4 as uuidv4 } from 'uuid';
import { RemoveOwnerModal } from '../../components/RemoveOwnerModal';
import { useUpdateApplication } from 'utils/useUpdateApplication';

export const OwnerInformation: FC = () => {
  const dispatch = useDispatch();
  const application = useSelector((state: RootState) => state?.applicationReducer?.factoringApplication);
  const isSoleProp = application?.applicationData?.company?.entityType === 'Sole Proprietor';
  const allowAdditionalOwners =
    application?.applicationData?.applicants?.length &&
    application.applicationData.applicants.length < 3 &&
    !isSoleProp;
  const primaryApplicant = application?.applicationData?.applicants?.[0];
  const formRef = useRef<HTMLDivElement>(null);
  const [isPatching, setIsPatching] = useState(false);
  const [localSSN, setLocalSSN] = useState<string | undefined>(primaryApplicant?.ssnOrItin);
  const [editingSSN, setEditingSSN] = useState(false);
  const [showRemoveOwnerModal, setShowRemoveOwnerModal] = useState(false);
  const { patchApplication, patchFullApplication, patchApplicationPullData } = useFormNetworkCalls(
    DO_APPLICATION_TYPE.FACTORING,
  );

  const { t } = useTranslation();
  const { AddCircleIcon } = DriftIcons;

  const getValueSSN = (applicant: Partial<Applicant> | undefined, error: string | undefined) => {
    if (editingSSN || error) {
      return ssnFormatter(localSSN || '');
    }
    const ssnOrItinDisplay = applicant?.ssnOrItinDisplay || '';
    const ssnOrItin = applicant?.ssnOrItin || localSSN || '';
    if (ssnOrItinDisplay.length > 0) {
      return maskSSN(ssnOrItinDisplay, true);
    } else {
      return ssnOrItin.length < 8 ? ssnFormatter(ssnOrItin) : maskSSN(ssnOrItin);
    }
  };

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const updateApplication = useUpdateApplication(DO_APPLICATION_TYPE.FACTORING);
  const {
    binaryHandlerGenerator,
    textHandlerGenerator,
    numberHandlerGenerator,
    selectDropdownHandlerGenerator,
    dateHandlerGenerator,
    regexValidatorGenerator,
    dateRangeValidatorGenerator,
    presenceValidatorGenerator,
    numberValidatorGenerator,
    clearValidation,
    handleInputFocus,
  } = useSectionFormHelpers({
    errorSetter: setErrors,
    patchApplication,
    updateApplication,
    application,
    fireBaseLogEvent: sendDOFirebaseEvent,
  });

  const fieldValidations = (index: number): { [key: string]: () => boolean } => {
    const validations = {
      firstName: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].firstName`,
        regex: nameRegex,
        errorText: t('first_name_error'),
      }),
      lastName: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].lastName`,
        regex: nameRegex,
        errorText: t('first_name_error'),
      }),
      email: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].email`,
        regex: emailRegex,
        errorText: t('invalid_email'),
        makeLowerCase: true,
        additionalErrorCheck: (value?: string) => {
          if (!value) return undefined;
          const duplicateIndices = findDuplicateIndices('email', application?.applicationData?.applicants, true);
          return duplicateIndices.includes(index) ? t('owner_info.error.email_duplicate') : undefined;
        },
      }),
      jobTitle: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].jobTitle`,
        regex: jobTitleRegex,
        errorText: t('owner_info.error.job_title'),
      }),
      mobilePhoneNumber: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].mobilePhoneNumber`,
        regex: phoneRegex,
        errorText: t('owner_info.error.mobile_phone_number'),
        additionalErrorCheck: (value?: string) => {
          if (!value) return undefined;
          const duplicateIndices = findDuplicateIndices('mobilePhoneNumber', application?.applicationData?.applicants);
          return duplicateIndices.includes(index) ? t('owner_info.error.phone_duplicate') : undefined;
        },
      }),
      ownershipPercentage: isSoleProp
        ? () => true
        : numberValidatorGenerator({
            key: `applicationData.applicants[${index}].ownershipPercentage`,
            range: [1, 100],
            errorText: t('owner_info.error.ownership_percentage'),
            optional: isSoleProp,
          }),
    };

    if (index === 0) {
      Object.assign(validations, {
        dateOfBirth: dateRangeValidatorGenerator({
          key: `applicationData.applicants[${index}].dateOfBirth`,
          range: [undefined, dayjs()],
          errorText: t('owner_info.error.dob'),
        }),
        ssnOrItin: regexValidatorGenerator({
          key: `applicationData.applicants[${index}].ssnOrItin`,
          regex: ssnOrItinRegex,
          errorText: t('owner_info.error.ssn'),
          skip: Boolean(primaryApplicant?.ssnOrItinDisplay),
          skipPatch: true,
          localValue: localSSN,
        }),

        homeStreetAddress: regexValidatorGenerator({
          key: `applicationData.applicants[${index}].homeAddress.street`,
          regex: streetRegex,
          errorText: t('invalid_street'),
        }),
        city: regexValidatorGenerator({
          key: `applicationData.applicants[${index}].homeAddress.city`,
          regex: cityRegex,
          errorText: t('invalid_city'),
        }),
        state: presenceValidatorGenerator({
          key: `applicationData.applicants[${index}].homeAddress.state`,
          errorText: t('invalid_state'),
        }),
        zipCode: regexValidatorGenerator({
          key: `applicationData.applicants[${index}].homeAddress.zip`,
          regex: zipRegex,
          errorText: t('invalid_zip'),
        }),
        county: regexValidatorGenerator({
          key: `applicationData.applicants[${index}].homeAddress.county`,
          regex: countyRegex,
          errorText: t('invalid_county'),
        }),

        previousEmployer: regexValidatorGenerator({
          key: `applicationData.applicants[${index}].previousEmployer`,
          regex: previousEmployerRegex,
          errorText: t('owner_info.error.previous_employer'),
        }),
      });
    }
    return validations;
  };

  const onChangeSSN = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    updateApplication(`applicationData.applicants[0].ssnOrItinDisplay`, '');
    setLocalSSN(editingSSN || value.length === 1 ? value.replace(/[^0-9-]/g, '') : '');
    clearValidation(`applicationData.applicants[0].ssnOrItin`);
    setEditingSSN(true);
  };

  const onBlurSSN = async () => {
    const isValid = await fieldValidations(0)?.ssnOrItin();
    setEditingSSN(false);
    if (isValid) {
      const applicantKey = primaryApplicant?.applicantId || primaryApplicant?.key;
      patchApplication(`applicationData.applicants[0].ssnOrItin`, localSSN, applicantKey);
    }
  };

  const scrollToFirstError = () => {
    const firstErrorInput = formRef.current?.querySelector('.Mui-error') as HTMLElement;
    if (firstErrorInput) {
      firstErrorInput.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      firstErrorInput.focus({ preventScroll: true });
    }
  };

  const handleContinueClick = async () => {
    const isValid = await checkFormValidation();

    if (isValid) {
      return;
    } else {
      scrollToFirstError();
    }
  };

  const checkFormValidation = async () => {
    let firstInvalidApplicant = -1;
    application.applicationData?.applicants?.forEach((_applicant, index) => {
      const results = Object.values(fieldValidations(index)).map((validator) => validator());

      if (firstInvalidApplicant === -1 && results.some((isValid) => !isValid)) {
        firstInvalidApplicant = index;
      }
    });

    if (firstInvalidApplicant !== -1) {
      scrollToFirstError();
      return false;
    } else {
      await patchFullApplication(setIsPatching);
      return true;
    }
  };

  let scrollY = 0;

  const lockBodyScroll = async () => {
    handleDatePickerFocus();
    scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${scrollY}px`;
  };

  const unlockBodyScroll = () => {
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    window.scrollTo(0, scrollY);
  };

  const handleOnClickSelect = (name: string) => {
    sendDOFirebaseEvent(AnalyticsEvents.EDIT, AnalyticsPages.FACTORING_APPLICATION_FORM, {
      description: `${name} input field`,
      field: name,
      applicationType: application.type ?? 'unknown',
      applicationId: application._id ?? 'unknown',
      user_email: primaryApplicant?.email ?? 'unknown',
    });
  };

  const handleDatePickerFocus = () => {
    sendDOFirebaseEvent(AnalyticsEvents.EDIT, AnalyticsPages.FACTORING_APPLICATION_FORM, {
      description: 'dateOfBirth input field',
      field: 'dateOfBirth',
      applicationType: application.type ?? 'unknown',
      applicationId: application._id ?? 'unknown',
      user_email: primaryApplicant?.email ?? 'unknown',
    });
  };

  const addNewOwner = () => {
    const newApplicantKey = uuidv4();
    dispatch({
      type: 'ADD_ADDITIONAL_OWNER',
      ownerKey: newApplicantKey,
      applicationType: DO_APPLICATION_TYPE.FACTORING,
    });
    patchApplication(
      `applicationData.applicants[${application.applicationData?.applicants?.length}].key`,
      newApplicantKey,
    );
  };

  const removeOwner = async (applicant: Partial<Applicant>) => {
    try {
      setShowRemoveOwnerModal(true);
      const updatedErrors = { ...errors };
      const filteredErrorKeys = Object.keys(updatedErrors).filter(
        (key) => key.includes('applicationData.applicants[1]') || key.includes('applicationData.applicants[2]'),
      );

      for (const key in updatedErrors) {
        if (filteredErrorKeys.includes(key)) {
          delete updatedErrors[key];
        }
      }
      setErrors(updatedErrors);
      if (application && application._id && applicant.key) {
        dispatch({ type: 'REMOVE_ADDITIONAL_OWNER', owner: applicant, applicationType: DO_APPLICATION_TYPE.FACTORING });
        await patchApplicationPullData(application._id, {
          'applicationData.applicants': { key: applicant.key },
        });
      }
    } catch {
      //noop
    } finally {
      setShowRemoveOwnerModal(false);
    }
  };

  return (
    <div ref={formRef} className={styles.formFieldContainer}>
      <div>
        <h2 className={styles.title}>{t('owner_info.header_title')}</h2>
        <p className={styles.description}>{t('owner_info.header_description')}</p>
        <p className={styles.description}>{t('owner_info.confirm_name')}</p>
      </div>
      {application?.applicationData?.applicants &&
        application?.applicationData?.applicants?.map((applicant, index) => {
          return index === 0 ? (
            <Fragment key={index}>
              <div className={styles.groupedFields}>
                <TextField
                  label={t('owner_info.first_name')}
                  name="firstName"
                  value={applicant?.firstName ?? ''}
                  onChange={textHandlerGenerator(`applicationData.applicants[${index}].firstName`, clearValidation)}
                  onBlur={fieldValidations(index).firstName}
                  error={!!errors[`applicationData.applicants[${index}].firstName`]}
                  onFocus={handleInputFocus}
                  helperText={errors[`applicationData.applicants[${index}].firstName`] ?? ' '}
                  slotProps={{ htmlInput: { maxLength: 50, autoCapitalize: 'sentences' } }}
                />
                <TextField
                  label={t('owner_info.last_name')}
                  name="lastName"
                  onFocus={handleInputFocus}
                  value={applicant?.lastName ?? ''}
                  onChange={textHandlerGenerator(`applicationData.applicants[${index}].lastName`, clearValidation)}
                  onBlur={fieldValidations(index).lastName}
                  error={!!errors[`applicationData.applicants[${index}].lastName`]}
                  helperText={errors[`applicationData.applicants[${index}].lastName`] ?? ' '}
                  slotProps={{ htmlInput: { maxLength: 50, autoCapitalize: 'sentences' } }}
                />
              </div>
              <div className={styles.groupedFields}>
                <div className={styles.smallFields}>
                  <FormControl>
                    <InputLabel id="owner-suffix-label">{t('owner_info.suffix')}</InputLabel>
                    <Select
                      labelId="owner-suffix-label"
                      label={t('owner_info.suffix')}
                      onOpen={() => handleOnClickSelect('ownerSuffix')}
                      onFocus={handleInputFocus}
                      name="ownerSuffix"
                      value={applicant?.suffix ?? ''}
                      onChange={selectDropdownHandlerGenerator(
                        `applicationData.applicants[${index}].suffix`,
                        clearValidation,
                      )}
                      fullWidth
                    >
                      <MenuItem value="Jr.">Jr.</MenuItem>
                      <MenuItem value="Sr.">Sr.</MenuItem>
                      <MenuItem value="II">II</MenuItem>
                      <MenuItem value="III">III</MenuItem>
                      <MenuItem value="IV">IV</MenuItem>
                      <MenuItem value="V">V</MenuItem>
                      {applicant?.suffix && <MenuItem value="">None</MenuItem>}
                    </Select>
                  </FormControl>
                  <DatePickers.DatePicker
                    label={t('owner_info.date_of_birth')}
                    desktopModeMediaQuery="@media (min-width: 768px)"
                    value={applicant?.dateOfBirth ? dayjs(applicant?.dateOfBirth) : null}
                    onChange={dateHandlerGenerator(`applicationData.applicants[${index}].dateOfBirth`, clearValidation)}
                    onAccept={fieldValidations(0).dateOfBirth}
                    disableFuture
                    slotProps={{
                      textField: {
                        error: !!errors[`applicationData.applicants[${index}].dateOfBirth`],
                        helperText: errors[`applicationData.applicants[${index}].dateOfBirth`] ?? ' ',
                        onBlur: fieldValidations(0).dateOfBirth,
                        onFocus: handleDatePickerFocus,
                      },
                    }}
                    openTo="year"
                    views={['year', 'month', 'day']}
                    onOpen={lockBodyScroll}
                    onClose={unlockBodyScroll}
                  />
                </div>
                <div className={styles.tooltipField}>
                  <TextField
                    name="ssnOrItin"
                    onFocus={handleInputFocus}
                    label={t('owner_info.ssn_or_itin')}
                    value={getValueSSN(applicant, errors[`applicationData.applicants[${index}].ssnOrItin`])}
                    onChange={onChangeSSN}
                    onBlur={onBlurSSN}
                    error={!!errors[`applicationData.applicants[${index}].ssnOrItin`]}
                    helperText={errors[`applicationData.applicants[${index}].ssnOrItin`] ?? ' '}
                    slotProps={{ htmlInput: { maxLength: 11, inputMode: 'numeric' } }}
                    fullWidth
                  />
                  <div className={styles.tooltipContainer}>
                    <Tooltip
                      title={t('owner_info.ssn_tooltip')}
                      placement="top"
                      enterTouchDelay={0}
                      leaveTouchDelay={8000}
                      slotProps={{
                        tooltip: {
                          className: styles.tooltip,
                        },
                      }}
                    >
                      <img
                        src={TooltipIcon}
                        alt="Information Icon"
                        width={24}
                        height={24}
                        className={styles.tooltipIcon}
                        tabIndex={0}
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
              <div className={styles.groupedFields}>
                <TextField
                  name="mobilePhoneNumber"
                  onFocus={handleInputFocus}
                  label={t('owner_info.mobile_phone')}
                  value={phoneFormatter(applicant?.mobilePhoneNumber) ?? ''}
                  onChange={numberHandlerGenerator(
                    `applicationData.applicants[${index}].mobilePhoneNumber`,
                    clearValidation,
                  )}
                  onBlur={fieldValidations(index).mobilePhoneNumber}
                  error={!!errors[`applicationData.applicants[${index}].mobilePhoneNumber`]}
                  helperText={errors[`applicationData.applicants[${index}].mobilePhoneNumber`] ?? ' '}
                  slotProps={{ htmlInput: { maxLength: 16, inputMode: 'numeric' } }}
                />
                <TextField
                  name="homeStreetAddress"
                  onFocus={handleInputFocus}
                  label={t('owner_info.street_address')}
                  value={applicant?.homeAddress?.street ?? ''}
                  onChange={textHandlerGenerator(
                    `applicationData.applicants[${index}].homeAddress.street`,
                    clearValidation,
                  )}
                  onBlur={fieldValidations(index).homeStreetAddress}
                  error={!!errors[`applicationData.applicants[${index}].homeAddress.street`]}
                  slotProps={{ htmlInput: { minLength: 5, maxLength: 100, autoCapitalize: 'sentences' } }}
                  helperText={errors[`applicationData.applicants[${index}].homeAddress.street`] ?? ' '}
                />
              </div>
              <div className={styles.groupedFields}>
                <TextField
                  name="city"
                  onFocus={handleInputFocus}
                  label={t('owner_info.city')}
                  value={applicant?.homeAddress?.city ?? ''}
                  onChange={textHandlerGenerator(
                    `applicationData.applicants[${index}].homeAddress.city`,
                    clearValidation,
                  )}
                  onBlur={fieldValidations(index).city}
                  error={!!errors[`applicationData.applicants[${index}].homeAddress.city`]}
                  helperText={errors[`applicationData.applicants[${index}].homeAddress.city`] ?? ' '}
                  slotProps={{ htmlInput: { minLength: 3, maxLength: 50, autoCapitalize: 'sentences' } }}
                />
                <div className={styles.smallFields}>
                  <FormControl error={!!errors[`applicationData.applicants[${index}].homeAddress.state`]}>
                    <InputLabel id="owner-state-label">{t('owner_info.state')}</InputLabel>
                    <Select
                      onOpen={() => handleOnClickSelect('ownerState')}
                      onFocus={handleInputFocus}
                      name="ownerState"
                      labelId="owner-state-label"
                      label={t('owner_info.state')}
                      value={applicant?.homeAddress?.state ?? ''}
                      onChange={selectDropdownHandlerGenerator(
                        `applicationData.applicants[${index}].homeAddress.state`,
                        clearValidation,
                      )}
                      fullWidth
                    >
                      {STATE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors[`applicationData.applicants[${index}].homeAddress.state`] ?? ' '}
                    </FormHelperText>
                  </FormControl>
                  <TextField
                    name="zipCode"
                    onFocus={handleInputFocus}
                    label={t('owner_info.zip_code')}
                    value={applicant?.homeAddress?.zip ?? ''}
                    onChange={numberHandlerGenerator(
                      `applicationData.applicants[${index}].homeAddress.zip`,
                      clearValidation,
                    )}
                    onBlur={fieldValidations(index).zipCode}
                    error={!!errors[`applicationData.applicants[${index}].homeAddress.zip`]}
                    helperText={errors[`applicationData.applicants[${index}].homeAddress.zip`] ?? ' '}
                    slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }}
                  />
                </div>
              </div>
              <div className={styles.groupedFields}>
                <TextField
                  label={t('email_address')}
                  value={applicant?.email ?? ''}
                  disabled
                  error={!!errors[`applicationData.applicants[${index}].email`]}
                  helperText={errors[`applicationData.applicants[${index}].email`] ?? ' '}
                />
                <TextField
                  name="county"
                  onFocus={handleInputFocus}
                  label={t('owner_info.local_county')}
                  value={applicant?.homeAddress?.county ?? ''}
                  onChange={textHandlerGenerator(
                    `applicationData.applicants[${index}].homeAddress.county`,
                    clearValidation,
                  )}
                  onBlur={fieldValidations(index).county}
                  error={!!errors[`applicationData.applicants[${index}].homeAddress.county`]}
                  helperText={errors[`applicationData.applicants[${index}].homeAddress.county`] ?? ' '}
                  slotProps={{ htmlInput: { minLength: 3, maxLength: 50, autoCapitalize: 'sentences' } }}
                />
              </div>
              <div className={styles.groupedFields}>
                <div className={styles.smallFields}>
                  <TextField
                    name="jobTitle"
                    onFocus={handleInputFocus}
                    label={t('owner_info.job_title')}
                    value={applicant?.jobTitle ?? ''}
                    onChange={textHandlerGenerator(`applicationData.applicants[${index}].jobTitle`, clearValidation)}
                    onBlur={fieldValidations(index).jobTitle}
                    error={!!errors[`applicationData.applicants[${index}].jobTitle`]}
                    helperText={errors[`applicationData.applicants[${index}].jobTitle`] ?? ' '}
                    slotProps={{ htmlInput: { minLength: 3, maxLength: 50, autoCapitalize: 'sentences' } }}
                  />
                  {!isSoleProp && (
                    <TextField
                      label={t('owner_info.ownership_percentage')}
                      value={applicant?.ownershipPercentage?.toString() || ''}
                      onChange={numberHandlerGenerator(
                        `applicationData.applicants[${index}].ownershipPercentage`,
                        clearValidation,
                      )}
                      onBlur={fieldValidations(index).ownershipPercentage}
                      error={!!errors[`applicationData.applicants[${index}].ownershipPercentage`]}
                      helperText={errors[`applicationData.applicants[${index}].ownershipPercentage`] ?? ' '}
                      slotProps={{ htmlInput: { maxLength: 3, inputMode: 'numeric' } }}
                    />
                  )}
                </div>
                <TextField
                  name="previousEmployer"
                  onFocus={handleInputFocus}
                  label={t('owner_info.previous_employer')}
                  value={applicant?.previousEmployer ?? ''}
                  onChange={textHandlerGenerator(
                    `applicationData.applicants[${index}].previousEmployer`,
                    clearValidation,
                  )}
                  onBlur={fieldValidations(index).previousEmployer}
                  error={!!errors[`applicationData.applicants[${index}].previousEmployer`]}
                  helperText={errors[`applicationData.applicants[${index}].previousEmployer`] ?? ' '}
                  slotProps={{ htmlInput: { maxLength: 50, autoCapitalize: 'sentences' } }}
                />
              </div>
              <div className={styles.groupedFields}>
                <div className={styles.questionContainer}>
                  <p className={styles.optionalQuestionTitle}>{t('owner_info.veteran_description')}</p>
                  <p className={styles.optionalText}>{t('optional')}</p>
                  <FormControl>
                    <RadioGroup
                      onFocus={handleInputFocus}
                      name="veteranOrServiceMember"
                      value={binaryValueFormatter(applicant?.veteranOrServiceMember)}
                      onChange={binaryHandlerGenerator(
                        `applicationData.applicants[${index}].veteranOrServiceMember`,
                        clearValidation,
                      )}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label={t('yes')} />
                      <FormControlLabel value="no" control={<Radio />} label={t('no')} />
                    </RadioGroup>
                  </FormControl>
                </div>
              </div>
            </Fragment>
          ) : (
            <Fragment key={index}>
              <div className={ownerStyles.secondaryOwnerHeader}>
                <h2 className={styles.title}>{t('owner_info.secondary_header_title', { index: index + 1 })}</h2>
                <Button onClick={() => removeOwner(applicant)} className={ownerStyles.removeSecondaryText}>
                  {t('owner_info.remove_owner')}
                </Button>
              </div>
              <div className={styles.groupedFields}>
                <div className={styles.smallFields}>
                  <TextField
                    label={t('owner_info.first_name')}
                    name="firstName"
                    value={applicant?.firstName ?? ''}
                    onChange={textHandlerGenerator(`applicationData.applicants[${index}].firstName`, clearValidation)}
                    onBlur={fieldValidations(index).firstName}
                    error={!!errors[`applicationData.applicants[${index}].firstName`]}
                    onFocus={handleInputFocus}
                    helperText={errors[`applicationData.applicants[${index}].firstName`] ?? ' '}
                    slotProps={{ htmlInput: { maxLength: 50, autoCapitalize: 'sentences' } }}
                  />
                  <TextField
                    label={t('owner_info.last_name')}
                    name="lastName"
                    onFocus={handleInputFocus}
                    value={applicant?.lastName ?? ''}
                    onChange={textHandlerGenerator(`applicationData.applicants[${index}].lastName`, clearValidation)}
                    onBlur={fieldValidations(index).lastName}
                    error={!!errors[`applicationData.applicants[${index}].lastName`]}
                    helperText={errors[`applicationData.applicants[${index}].lastName`] ?? ' '}
                    slotProps={{ htmlInput: { maxLength: 50, autoCapitalize: 'sentences' } }}
                  />
                </div>
                <TextField
                  name="mobilePhoneNumber"
                  onFocus={handleInputFocus}
                  label={t('owner_info.mobile_phone')}
                  value={phoneFormatter(applicant?.mobilePhoneNumber) ?? ''}
                  onChange={numberHandlerGenerator(
                    `applicationData.applicants[${index}].mobilePhoneNumber`,
                    clearValidation,
                  )}
                  onBlur={fieldValidations(index).mobilePhoneNumber}
                  error={!!errors[`applicationData.applicants[${index}].mobilePhoneNumber`]}
                  helperText={errors[`applicationData.applicants[${index}].mobilePhoneNumber`] ?? ' '}
                  slotProps={{ htmlInput: { maxLength: 16, inputMode: 'numeric' } }}
                />
              </div>
              <div className={styles.groupedFields}>
                <TextField
                  label={t('email_address')}
                  value={applicant?.email ?? ''}
                  onChange={textHandlerGenerator(`applicationData.applicants[${index}].email`, clearValidation, true)}
                  onBlur={fieldValidations(index).email}
                  error={!!errors[`applicationData.applicants[${index}].email`]}
                  helperText={errors[`applicationData.applicants[${index}].email`] ?? ' '}
                />
                <div className={styles.smallFields}>
                  <TextField
                    name="jobTitle"
                    onFocus={handleInputFocus}
                    label={t('owner_info.job_title')}
                    value={applicant?.jobTitle ?? ''}
                    onChange={textHandlerGenerator(`applicationData.applicants[${index}].jobTitle`, clearValidation)}
                    onBlur={fieldValidations(index).jobTitle}
                    error={!!errors[`applicationData.applicants[${index}].jobTitle`]}
                    helperText={errors[`applicationData.applicants[${index}].jobTitle`] ?? ' '}
                    slotProps={{ htmlInput: { minLength: 3, maxLength: 50, autoCapitalize: 'sentences' } }}
                  />
                  <TextField
                    label={t('owner_info.ownership_percentage')}
                    value={applicant?.ownershipPercentage?.toString() || ''}
                    onChange={numberHandlerGenerator(
                      `applicationData.applicants[${index}].ownershipPercentage`,
                      clearValidation,
                    )}
                    onBlur={fieldValidations(index).ownershipPercentage}
                    error={!!errors[`applicationData.applicants[${index}].ownershipPercentage`]}
                    helperText={errors[`applicationData.applicants[${index}].ownershipPercentage`] ?? ' '}
                    slotProps={{ htmlInput: { maxLength: 3, inputMode: 'numeric' } }}
                  />
                </div>
              </div>
            </Fragment>
          );
        })}
      {allowAdditionalOwners && (
        <>
          <Divider className={ownerStyles.divider} />
          <div className={ownerStyles.additionalOwnerContainer}>
            <p className={styles.questionTitle}>{t('owner_info.add_owner_title')}</p>
            <p className={styles.description}>{t('owner_info.add_owner_description')}</p>
            <IconButton className={ownerStyles.additionalOwnerButton} onClick={addNewOwner}>
              <AddCircleIcon color="success" />
            </IconButton>
          </div>
        </>
      )}
      <div className={styles.footer}>
        <ContinueButton handleContinueClick={handleContinueClick} isPatching={isPatching} />
      </div>
      <RemoveOwnerModal show={showRemoveOwnerModal} />
    </div>
  );
};

export default OwnerInformation;
